/**
 * API service — cliente REST tipado para el backend.
 */
import type {
  TestSystem, Version, Requirement, Scenario, TestCase, TestStep, DSLAction,
  ExecutionPlan, ExecutionRun, Agent, DashboardMetrics,
  AIGenerationRequest, AIGenerationResult,
} from '../types';

const BASE = '/api/v1';  // CRA proxy routes to backend:8000 (see package.json proxy)

class APIError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

// Check if JWT token is expired without making a request
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now() + 30_000; // 30s buffer
  } catch { return true; }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('access_token');

  // Proactive check — if token is expired before the request
  if (token && isTokenExpired(token)) {
    localStorage.removeItem('access_token');
    window.dispatchEvent(new CustomEvent('session:expired'));
    throw new APIError(401, 'Sesión expirada');
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  // Reactive check — 401 from server
  if (res.status === 401) {
    localStorage.removeItem('access_token');
    window.dispatchEvent(new CustomEvent('session:expired'));
    throw new APIError(401, 'Sesión expirada');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new APIError(res.status, body.message ?? res.statusText, body);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_id: string;
  email: string;
  role: string;
  tenant_id: string;
}

export const auth = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  me: () => request<{ id: string; email: string; role: string }>('/auth/me'),
};

// ─── Systems ──────────────────────────────────────────────────────────────────
export const systems = {
  list: () => request<TestSystem[]>('/systems'),
  get: (id: string) => request<TestSystem>(`/systems/${id}`),
  create: (data: Partial<TestSystem>) =>
    request<TestSystem>('/systems', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<TestSystem>) =>
    request<TestSystem>(`/systems/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/systems/${id}`, { method: 'DELETE' }),
  impact: (id: string) => request<Record<string, unknown>>(`/systems/${id}/impact`),
};

// ─── Versions ─────────────────────────────────────────────────────────────────
export const versions = {
  list: (system_id: string) => request<Version[]>(`/systems/${system_id}/versions`),
  create: (system_id: string, data: Partial<Version>) =>
    request<Version>(`/systems/${system_id}/versions`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (version_id: string, data: Partial<Version>) =>
    request<Version>(`/versions/${version_id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  delete: (version_id: string) =>
    request<void>(`/versions/${version_id}`, { method: 'DELETE' }),
  impact: (id: string) => request<Record<string, unknown>>(`/versions/${id}/impact`),
  activate: (version_id: string) =>
    request<Version>(`/versions/${version_id}/activate`, { method: 'POST' }),
  clone: (version_id: string, data?: { name?: string }) =>
    request<Version>(`/versions/${version_id}/clone`, {
      method: 'POST', body: JSON.stringify(data ?? {}),
    }),
};

// ─── Requirements ─────────────────────────────────────────────────────────────
export interface JiraIssuePreview {
  external_ref: string;
  title: string;
  description?: string | null;
  priority: string;
  issue_type: string;
  action: 'create' | 'update';
}
export interface JiraImportPreviewResult {
  issues: JiraIssuePreview[];
  jql_used: string;
}
export interface JiraImportResult {
  imported: number;
  updated: number;
  items: { external_ref: string; title: string; action: string; requirement_id: string }[];
}

export const requirements = {
  list: (version_id: string) => request<Requirement[]>(`/versions/${version_id}/requirements`),
  create: (version_id: string, data: Partial<Requirement>) =>
    request<Requirement>(`/versions/${version_id}/requirements`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (req_id: string, data: Partial<Requirement>) =>
    request<Requirement>(`/requirements/${req_id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  delete: (req_id: string) =>
    request<void>(`/requirements/${req_id}`, { method: 'DELETE' }),
  impact: (id: string) => request<Record<string, unknown>>(`/requirements/${id}/impact`),
  reorder: (version_id: string, ids: string[]) =>
    request<void>(`/versions/${version_id}/requirements/reorder`, {
      method: 'POST', body: JSON.stringify({ ordered_ids: ids }),
    }),
  previewJiraImport: (version_id: string, params: { jql?: string; issue_types?: string[]; max_results?: number }) =>
    request<JiraImportPreviewResult>(`/versions/${version_id}/requirements/import-jira/preview`, {
      method: 'POST', body: JSON.stringify(params),
    }),
  confirmJiraImport: (version_id: string, external_refs: string[]) =>
    request<JiraImportResult>(`/versions/${version_id}/requirements/import-jira`, {
      method: 'POST', body: JSON.stringify({ external_refs }),
    }),
};

// ─── Scenarios ────────────────────────────────────────────────────────────────
export const scenarios = {
  list: (req_id: string) => request<Scenario[]>(`/requirements/${req_id}/scenarios`),
  create: (req_id: string, data: Partial<Scenario>) =>
    request<Scenario>(`/requirements/${req_id}/scenarios`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (scenario_id: string, data: Partial<Scenario>) =>
    request<Scenario>(`/scenarios/${scenario_id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  delete: (scenario_id: string) =>
    request<void>(`/scenarios/${scenario_id}`, { method: 'DELETE' }),
  impact: (id: string) => request<Record<string, unknown>>(`/scenarios/${id}/impact`),
  reorder: (req_id: string, ids: string[]) =>
    request<void>(`/requirements/${req_id}/scenarios/reorder`, {
      method: 'POST', body: JSON.stringify({ ordered_ids: ids }),
    }),
  export: async (scenario_id: string, format: 'xlsx' | 'docx', filename: string) => {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${BASE}/scenarios/${scenario_id}/export?format=${format}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new APIError(res.status, 'No se pudo generar el archivo de exportación');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ─── Cases ────────────────────────────────────────────────────────────────────
export const datasetApi = {
  // ─── Dataset (Data Driven Testing) ───────────────────────────────────────
  getDataset: (scenarioId: string) =>
    request<Record<string, unknown> | null>(`/scenarios/${scenarioId}/dataset`),
  uploadDataset: (scenarioId: string, data: { name?: string; columns: string[]; rows: string[][]; active_rows?: boolean[] }) =>
    request<Record<string, unknown>>(`/scenarios/${scenarioId}/dataset`, { method: 'POST', body: JSON.stringify(data) }),
  updateDatasetCell: (scenarioId: string, data: { row_index: number; column: string; value: string }) =>
    request<void>(`/scenarios/${scenarioId}/dataset/cell`, { method: 'PATCH', body: JSON.stringify(data) }),
  updateDatasetRowActive: (scenarioId: string, data: { row_index: number; active: boolean }) =>
    request<void>(`/scenarios/${scenarioId}/dataset/row-active`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteDataset: (scenarioId: string) =>
    request<void>(`/scenarios/${scenarioId}/dataset`, { method: 'DELETE' }),
  getDatasetMapping: (caseId: string) =>
    request<{ mapping: Record<string, string> }>(`/cases/${caseId}/dataset-mapping`),
  saveDatasetMapping: (caseId: string, mapping: Record<string, string>) =>
    request<void>(`/cases/${caseId}/dataset-mapping`, { method: 'PUT', body: JSON.stringify({ mapping }) }),
};

export const cases = {
  list: (scenario_id: string) =>
    request<TestCase[]>(`/scenarios/${scenario_id}/cases`),
  get: (id: string) => request<TestCase>(`/cases/${id}`),
  create: (scenario_id: string, data: Partial<TestCase>) =>
    request<TestCase>(`/scenarios/${scenario_id}/cases`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<TestCase>) =>
    request<TestCase>(`/cases/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/cases/${id}`, { method: 'DELETE' }),
  impact: (id: string) => request<Record<string, unknown>>(`/cases/${id}/impact`),
  duplicate: (id: string) => request<TestCase>(`/cases/${id}/duplicate`, { method: 'POST' }),
  reorder: (scenario_id: string, ids: string[]) =>
    request<void>(`/scenarios/${scenario_id}/cases/reorder`, {
      method: 'POST', body: JSON.stringify({ ordered_ids: ids }),
    }),
};

// ─── Steps + Actions ──────────────────────────────────────────────────────────
export const steps = {
  list: (case_id: string) => request<TestStep[]>(`/cases/${case_id}/steps`),
  create: (case_id: string, data: Partial<TestStep>) =>
    request<TestStep>(`/cases/${case_id}/steps`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<TestStep>) =>
    request<TestStep>(`/steps/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/steps/${id}`, { method: 'DELETE' }),
  reorder: (case_id: string, ids: string[]) =>
    request<void>(`/cases/${case_id}/steps/reorder`, {
      method: 'POST', body: JSON.stringify({ ordered_ids: ids }),
    }),
  addAction: (step_id: string, action: Record<string, unknown>) =>
    request<DSLAction>(`/steps/${step_id}/actions`, {
      method: 'POST', body: JSON.stringify(action),
    }),
  updateAction: (step_id: string, action_id: string, data: Partial<DSLAction>) => {
    // Strip frontend-only fields (action_id, type) — backend expects target/params/on_failure/meta/timeout_ms
    const { action_id: _aid, type: _type, ...patch } = data as Record<string, unknown>;
    return request<DSLAction>(`/steps/${step_id}/actions/${action_id}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    });
  },
  deleteAction: (step_id: string, action_id: string) =>
    request<void>(`/steps/${step_id}/actions/${action_id}`, { method: 'DELETE' }),
  reorderActions: (step_id: string, ids: string[]) =>
    request<void>(`/steps/${step_id}/actions/reorder`, {
      method: 'POST', body: JSON.stringify({ ordered_ids: ids }),
    }),
};

// ─── Executions ───────────────────────────────────────────────────────────────
export const executions = {
  listPlans: () => request<ExecutionPlan[]>('/executions/plans'),
  getPlan: (id: string) => request<ExecutionPlan>(`/executions/plans/${id}`),
  createPlan: (data: Partial<ExecutionPlan>) =>
    request<ExecutionPlan>('/executions/plans', {
      method: 'POST', body: JSON.stringify(data),
    }),
  cancelPlan: (id: string) =>
    request<void>(`/executions/plans/${id}/cancel`, { method: 'POST' }),
  rerunPlan: (id: string) =>
    request<ExecutionPlan>(`/executions/plans/${id}/rerun`, { method: 'POST' }),
  updatePlan: (id: string, data: Partial<ExecutionPlan>) =>
    request<ExecutionPlan>(`/executions/plans/${id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  deletePlan: (id: string) =>
    request<void>(`/executions/plans/${id}`, { method: 'DELETE' }),
  getRuns: (plan_id: string, opts?: { all?: boolean }) =>
    request<ExecutionRun[]>(`/executions/plans/${plan_id}/runs${opts?.all ? '?all=true' : ''}`),
  getRun: (run_id: string) => request<ExecutionRun>(`/executions/runs/${run_id}`),
  getRunStepResults: (run_id: string) =>
    request<Record<string, unknown>>(`/executions/runs/${run_id}/step-results`),
};

// ─── AI ───────────────────────────────────────────────────────────────────────
export const ai = {
  generateCases: async (req: AIGenerationRequest): Promise<AIGenerationResult> => {
    const form = new FormData();
    form.append('scenario_id', req.scenario_id);
    if (req.context_text) form.append('context_text', req.context_text);
    if (req.generation_type) form.append('generation_type', req.generation_type);
    if (req.case_count) form.append('case_count', String(req.case_count));
    req.screenshots?.forEach((f) => form.append('screenshots', f));
    const token = localStorage.getItem('access_token');
    const res = await fetch(`/api/v1/ai/generate-cases`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new APIError(res.status, body.detail || `AI generation failed (${res.status})`, body);
    }
    return res.json();
  },

  complementContext: async (params: { scenario_id: string; scenario_title: string; context_text: string }): Promise<{ complemented_text: string }> => {
    const form = new FormData();
    form.append('scenario_id', params.scenario_id);
    form.append('scenario_title', params.scenario_title);
    form.append('context_text', params.context_text);
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${BASE}/ai/complement-context`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) throw new APIError(res.status, 'Complement failed');
    return res.json();
  },
};

// ─── Agents ───────────────────────────────────────────────────────────────────

export interface AgentRunSummary {
  run_id: string;
  plan_id: string;
  case_id: string;
  plan_name: string | null;
  status: string;
  total_steps: number;
  passed_steps: number;
  failed_steps: number;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
}

export const agentsApi = {
  list: () => request<Agent[]>('/agents').catch(() => [] as Agent[]),
  update: (id: string, data: { name: string }) =>
    request<Agent>(`/agents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<void>(`/agents/${id}`, { method: 'DELETE' }),
  getRuns: (id: string, limit = 20) =>
    request<AgentRunSummary[]>(`/agents/${id}/runs?limit=${limit}`).catch(() => [] as AgentRunSummary[]),
};

// ─── Metrics ──────────────────────────────────────────────────────────────────
export const metricsApi = {
  dashboard: (range_days = 30) =>
    request<DashboardMetrics>(`/metrics/dashboard?range_days=${range_days}`),
};

// ─── Settings ─────────────────────────────────────────────────────────────────
export const settingsApi = {
  // Organization
  getOrganization: () => request<Record<string, unknown>>('/settings/organization'),
  updateOrganization: (data: { name?: string; timezone?: string; language?: string }) =>
    request<Record<string, unknown>>('/settings/organization', {
      method: 'PATCH', body: JSON.stringify(data),
    }),

  // Users
  listUsers: () => request<Record<string, unknown>[]>('/settings/users'),
  inviteUser: (data: { email: string; full_name: string; role: string }) =>
    request<Record<string, unknown>>('/settings/users/invite', {
      method: 'POST', body: JSON.stringify(data),
    }),
  updateUser: (id: string, data: { full_name?: string; role?: string; is_active?: boolean }) =>
    request<Record<string, unknown>>(`/settings/users/${id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  deleteUser: (id: string) =>
    request<void>(`/settings/users/${id}`, { method: 'DELETE' }),
  changePassword: (data: { current_password: string; new_password: string }) =>
    request<void>('/settings/users/change-password', {
      method: 'POST', body: JSON.stringify(data),
    }),

  // Integrations

  // ─── AI Config ────────────────────────────────────────────────────────────
  getAIConfig: () =>
    request<Record<string, unknown>>('/settings/ai'),
  saveAIConfig: (data: { provider: string; model: string; api_key?: string; azure_endpoint?: string; azure_deployment?: string }) =>
    request<void>('/settings/ai', { method: 'PUT', body: JSON.stringify(data) }),
  testAIConfig: () =>
    request<Record<string, unknown>>('/settings/ai/test', { method: 'POST' }),
  listIntegrations: () => request<Record<string, unknown>>('/settings/integrations'),
  connectIntegration: (id: string, config: Record<string, string>) =>
    request<Record<string, unknown>>(`/settings/integrations/${id}/connect`, {
      method: 'POST', body: JSON.stringify(config),
    }),
  disconnectIntegration: (id: string) =>
    request<Record<string, unknown>>(`/settings/integrations/${id}`, { method: 'DELETE' }),
  testIntegration: (id: string) =>
    request<{ ok: boolean; detail?: string; account?: string; latency_ms: number }>(
      `/settings/integrations/${id}/test`, { method: 'POST' }
    ),
};

// ─── Page Object Model (POM) ───────────────────────────────────────────────────
export const pagesApi = {
  // Screen Pages
  listPages: (version_id: string) =>
    request<Record<string, unknown>[]>(`/versions/${version_id}/pages`),
  createPage: (version_id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/versions/${version_id}/pages`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  updatePage: (page_id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/pages/${page_id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  deletePage: (page_id: string) =>
    request<void>(`/pages/${page_id}`, { method: 'DELETE' }),

  // Page Objects
  listObjects: (page_id: string) =>
    request<Record<string, unknown>[]>(`/pages/${page_id}/objects`),
  createObject: (page_id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/pages/${page_id}/objects`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  importObjects: (page_id: string, objects: Record<string, unknown>[], skip_duplicates = true) =>
    request<Record<string, unknown>>(`/pages/${page_id}/objects/import`, {
      method: 'POST', body: JSON.stringify({ objects, skip_duplicates }),
    }),
  updateObject: (object_id: string, data: Record<string, unknown>) =>
    request<Record<string, unknown>>(`/objects/${object_id}`, {
      method: 'PATCH', body: JSON.stringify(data),
    }),
  deleteObject: (object_id: string) =>
    request<void>(`/objects/${object_id}`, { method: 'DELETE' }),

  // Combo — all pages grouped by system for Inspector
  listByTenant: () =>
    request<Record<string, unknown>[]>('/pages/by-tenant'),

  // Get objects for a specific version (for ActionEditor combo)
  getObjectsForVersion: async (version_id: string): Promise<Record<string, unknown>[]> => {
    const pages = await request<Record<string, unknown>[]>(`/versions/${version_id}/pages`);
    const allObjects: Record<string, unknown>[] = [];
    for (const page of pages) {
      const objs = await request<Record<string, unknown>[]>(`/pages/${page['id']}/objects`);
      objs.forEach(o => allObjects.push({ ...o, page_name: page['name'] }));
    }
    return allObjects;
  },
};


// ─── Notifications ────────────────────────────────────────────────────────────

export interface AppNotification {
  id: string;
  type: 'exec_failed' | 'exec_completed' | 'agent_offline' | 'new_user' | 'ai_generation';
  title: string;
  message?: string;
  resource_type?: string;
  resource_id?: string;
  read: boolean;
  created_at: string;
}

export interface NotificationPrefs {
  exec_failed: boolean;
  exec_completed: boolean;
  agent_offline: boolean;
  new_user: boolean;
  ai_generation: boolean;
}

export const notificationsApi = {
  list: () => request<AppNotification[]>('/settings/notifications'),
  getPrefs: () => request<NotificationPrefs>('/settings/notifications/prefs'),
  savePrefs: (prefs: NotificationPrefs) =>
    request<void>('/settings/notifications/prefs', { method: 'PUT', body: JSON.stringify(prefs) }),
  markRead: (ids: string[]) =>
    request<void>('/settings/notifications/mark-read', { method: 'POST', body: JSON.stringify({ ids }) }),
  markAllRead: () =>
    request<void>('/settings/notifications/mark-read', { method: 'POST', body: JSON.stringify({ all: true }) }),
};


// ─── Defects ──────────────────────────────────────────────────────────────────

export interface DefectCreatePayload {
  title: string;
  description?: string;
  steps_to_reproduce?: string;
  error_summary?: string;
  severity?: string;
  priority?: string;
  environment?: string;
  assigned_to?: string;
  execution_run_id?: string;
  test_case_id?: string;
  test_case_title?: string;
  screenshot_refs?: { url?: string; thumbnail_url?: string }[];
}

export const defectsApi = {
  list: (params?: { status?: string; severity?: string; execution_run_id?: string }) => {
    const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([,v]) => v) as string[][]).toString() : '';
    return request<import('../types').Defect[]>(`/defects${qs}`);
  },
  get: (id: string) => request<import('../types').Defect>(`/defects/${id}`),
  create: (data: DefectCreatePayload) =>
    request<import('../types').Defect>('/defects', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<DefectCreatePayload & { status_comment: string }>) =>
    request<import('../types').Defect>(`/defects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  changeStatus: (id: string, status: string, comment?: string) =>
    request<import('../types').Defect>(`/defects/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, comment }),
    }),
  delete: (id: string) => request<void>(`/defects/${id}`, { method: 'DELETE' }),
  reportToJira: (id: string) =>
    request<import('../types').Defect>(`/defects/${id}/jira/report`, { method: 'POST' }),
  syncJiraStatus: (id: string) =>
    request<import('../types').Defect>(`/defects/${id}/jira/sync`, { method: 'POST' }),
};